import { useState, useEffect, useRef, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface UserProfile {
  id: string;
  email: string;
  name: string | null;
  role: string | null;
  subscription_status: string | null;
  is_banned: boolean | null;
  created_at: string;
  updated_at: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_id: string | null;
  price_id: string | null;
  plan_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  last_payment_date: string | null;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  isAdmin: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthMiddlewareProps {
  children: ReactNode;
  onAuthStateChange?: (authState: AuthState) => void;
}

const AuthMiddleware = ({ children, onAuthStateChange }: AuthMiddlewareProps) => {
  // Try to load cached auth state from localStorage
  const getCachedAuthState = (): AuthState => {
    try {
      const cached = localStorage.getItem('authState');
      if (cached) {
        const parsed = JSON.parse(cached);
        console.log('🛡️ AuthMiddleware: Loaded cached auth state:', {
          isAuthenticated: parsed.isAuthenticated,
          isAdmin: parsed.isAdmin,
          email: parsed.profile?.email
        });
        return { ...parsed, isLoading: false };
      }
    } catch (error) {
      console.warn('🛡️ AuthMiddleware: Failed to load cached auth state:', error);
    }
    return {
      user: null,
      session: null,
      profile: null,
      isAdmin: false,
      isAuthenticated: false,
      isLoading: true,
    };
  };

  const [authState, setAuthState] = useState<AuthState>(getCachedAuthState());

  const navigate = useNavigate();
  const location = useLocation();
  const hasInitialized = useRef(false);

  // Cache auth state for faster subsequent loads across tabs
  const cacheAuthState = (state: AuthState) => {
    try {
      localStorage.setItem('authState', JSON.stringify(state));
    } catch (error) {
      console.warn('🛡️ AuthMiddleware: Failed to cache auth state:', error);
    }
  };

  // Clear cached profile data
  const clearCachedProfile = (userId?: string) => {
    try {
      if (userId) {
        localStorage.removeItem(`userProfile_${userId}`);
      } else {
        // Clear all cached profiles
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('userProfile_')) {
            localStorage.removeItem(key);
          }
        });
      }
    } catch (error) {
      console.warn('🛡️ AuthMiddleware: Failed to clear cached profile:', error);
    }
  };

  // Remove excessive logging for performance

  // Load user profile with enhanced caching
  const loadUserProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      console.log('🛡️ AuthMiddleware: Loading profile for user:', userId);

      // Check if we have a cached profile for this user
      const cachedProfileKey = `userProfile_${userId}`;
      const cachedProfile = localStorage.getItem(cachedProfileKey);

      if (cachedProfile) {
        try {
          const parsed = JSON.parse(cachedProfile);
          console.log('🛡️ AuthMiddleware: Using cached profile for user:', userId);
          return parsed;
        } catch (error) {
          console.warn('🛡️ AuthMiddleware: Failed to parse cached profile, fetching fresh');
          localStorage.removeItem(cachedProfileKey);
        }
      }

      // Add timeout to profile loading
      const profilePromise = supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile timeout')), 10000) // Back to 10 seconds
      );

      const { data: profileData, error } = await Promise.race([profilePromise, timeoutPromise]) as any;

      if (error) {
        console.error('🛡️ AuthMiddleware: Profile error:', error);
        return null;
      }

      if (!profileData) {
        console.warn('🛡️ AuthMiddleware: No profile data for user:', userId);
        return null;
      }

      console.log('✅ AuthMiddleware: Profile loaded:', {
        email: profileData.email,
        role: profileData.role,
        id: profileData.id
      });

      // Cache the profile for faster subsequent loads
      localStorage.setItem(cachedProfileKey, JSON.stringify(profileData));

      return profileData;
    } catch (error) {
      console.error('🛡️ AuthMiddleware: Profile fetch failed:', error);
      return null;
    }
  };

  // Initialize auth state
  const initializeAuth = async () => {
    try {
      console.log('🛡️ AuthMiddleware: Initializing auth...');

      // Clear any stale cached auth state first
      localStorage.removeItem('authState');

      // Get current session with timeout to prevent hanging
      const sessionPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Session timeout')), 10000)
      );

      const { data: { session }, error } = await Promise.race([sessionPromise, timeoutPromise]) as any;

      if (error) {
        console.error('🛡️ AuthMiddleware: Session error:', error);
        const errorState = {
          user: null,
          session: null,
          profile: null,
          isAdmin: false,
          isAuthenticated: false,
          isLoading: false
        };
        setAuthState(errorState);
        cacheAuthState(errorState);
        return;
      }

      if (!session) {
        console.log('🛡️ AuthMiddleware: No session found');
        const noSessionState = {
          user: null,
          session: null,
          profile: null,
          isAdmin: false,
          isAuthenticated: false,
          isLoading: false
        };
        setAuthState(noSessionState);
        cacheAuthState(noSessionState);
        return;
      }

      console.log('🛡️ AuthMiddleware: Session found for:', session.user.email);

      // Verify session is still valid by checking expiry
      const now = Math.floor(Date.now() / 1000);
      if (session.expires_at && session.expires_at < now) {
        console.log('🛡️ AuthMiddleware: Session expired, signing out...');
        await supabase.auth.signOut();
        return;
      }

      // Load user profile
      const profile = await loadUserProfile(session.user.id);
      const isAdmin = profile?.role === 'admin';

      // Update auth state with complete information
      const newAuthState: AuthState = {
        user: session.user,
        session,
        profile,
        isAdmin,
        isAuthenticated: true,
        isLoading: false,
      };

      setAuthState(newAuthState);
      cacheAuthState(newAuthState);

      console.log('✅ AuthMiddleware: Auth initialized:', {
        email: session.user.email,
        role: profile?.role,
        isAdmin,
        isAuthenticated: true,
        expiresAt: new Date(session.expires_at! * 1000).toISOString()
      });

      // Notify parent component of auth state change
      onAuthStateChange?.(newAuthState);

    } catch (error) {
      console.error('🛡️ AuthMiddleware: Initialization failed:', error);
      const errorState = {
        user: null,
        session: null,
        profile: null,
        isAdmin: false,
        isAuthenticated: false,
        isLoading: false
      };
      setAuthState(errorState);
      cacheAuthState(errorState);
    }
  };

  // Handle auth state changes
  const handleAuthStateChange = async (event: string, session: Session | null) => {
    console.log('🛡️ AuthMiddleware: Auth state changed:', event, !!session);

    if (!session) {
      // User logged out - clear all cached data
      clearCachedProfile(); // Clear all cached profiles
      const logoutState = {
        user: null,
        session: null,
        profile: null,
        isAdmin: false,
        isAuthenticated: false,
        isLoading: false,
      };
      setAuthState(logoutState);
      cacheAuthState(logoutState);
      return;
    }

    // User logged in or session refreshed
    const profile = await loadUserProfile(session.user.id);
    const isAdmin = profile?.role === 'admin';

    const newAuthState: AuthState = {
      user: session.user,
      session,
      profile,
      isAdmin,
      isAuthenticated: true,
      isLoading: false,
    };

    setAuthState(newAuthState);
    cacheAuthState(newAuthState);
    onAuthStateChange?.(newAuthState);

    console.log('✅ AuthMiddleware: Auth state updated:', {
      email: session.user.email,
      role: profile?.role,
      isAdmin
    });
  };

  // Route protection logic
  const checkRouteAccess = (path: string): boolean => {
    // Public routes that don't require authentication (exact matches or specific prefixes)
    const publicRoutes = ['/portal/auth', '/portal/signup', '/pricing', '/chat/', '/fullscreen-chat/'];
    const isHomePage = path === '/'; // Only exact home page is public

    const isPublicRoute = publicRoutes.some(route => path.startsWith(route)) || isHomePage;

    // Route access check - reduced logging for performance

    // If it's a public route, allow access
    if (isPublicRoute) {
      console.log('🛡️ AuthMiddleware: Public route - access granted');
      return true;
    }

    // If user is not authenticated, deny access to all protected routes
    if (!authState.isAuthenticated) {
      console.log('🛡️ AuthMiddleware: Not authenticated - access denied');
      return false;
    }

    // If it's an admin route, check admin role
    if (path.startsWith('/admin')) {
      const hasAdminAccess = authState.isAdmin;
      console.log('🛡️ AuthMiddleware: Admin route - access:', hasAdminAccess ? 'granted' : 'denied');
      return hasAdminAccess;
    }

    // For all other protected routes, just require authentication
    console.log('🛡️ AuthMiddleware: Protected route - access granted (authenticated)');
    return authState.isAuthenticated;
  };

  // Initialize auth on mount - always check for valid session
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;

      // Always initialize auth to check for valid session
      // This ensures we don't rely on potentially stale cached data
      console.log('🛡️ AuthMiddleware: Initializing auth (checking for valid session)...');
      initializeAuth();
    }

    // Failsafe: Force loading to false after timeout
    const loadingTimeout = setTimeout(() => {
      console.warn('⚠️ AuthMiddleware: Forcing loading to false after timeout');
      setAuthState(prev => ({ ...prev, isLoading: false }));
    }, 8000);

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(handleAuthStateChange);

    return () => {
      subscription.unsubscribe();
      clearTimeout(loadingTimeout);
    };
  }, []);

  // Handle route protection
  useEffect(() => {
    if (authState.isLoading) {
      return; // Still loading, don't redirect yet
    }

    const currentPath = location.pathname;
    const hasAccess = checkRouteAccess(currentPath);

    console.log('🛡️ AuthMiddleware: Route access check:', {
      path: currentPath,
      hasAccess,
      isAuthenticated: authState.isAuthenticated,
      isAdmin: authState.isAdmin
    });

    if (!hasAccess) {
      if (!authState.isAuthenticated) {
        // Not authenticated, redirect to login
        console.log('🛡️ AuthMiddleware: Redirecting to login - not authenticated');
        navigate('/portal/auth');
      } else if (currentPath.startsWith('/admin') && !authState.isAdmin) {
        // Not admin, redirect to portal
        console.log('🛡️ AuthMiddleware: Redirecting to portal - not admin');
        navigate('/portal');
      }
    }
  }, [authState, location.pathname, navigate]);

  // Always render children - let individual components handle loading states
  // This prevents the HTML fallback error from triggering
  return <>{children}</>;
};

export default AuthMiddleware;
export type { AuthState, UserProfile };
